import React, { useRef, useEffect } from 'react';
import { User, Hash, MapPin, Search, Layers, List, Film, MessageSquare } from "lucide-react";
import Spinner from 'components/common/Spinner';
import GuideTooltip from 'components/common/GuideTooltip';
import { useTutorial, TutorialStep } from 'contexts/TutorialContext';
import { useTranslation } from 'react-i18next';

interface Props {
  chips: string[];
  selectedChip: string | null;
  handleSelectChip: (chip: string) => void;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  addFilterItem: () => void;
  audienceItems: any[];
  selectedAudience: any[];
  handleAudienceChange: (id: string, checked: boolean, name: string, pk: string, additionalData?: { caption?: string; thumbnail?: string }) => void;
  filterItem: any[];
  removeFilterItem: (index: number) => void;
  setFilterItems: (items: any[]) => void;
  openModal: () => void;
  notify: (message: string) => void;
  isSearching?: boolean;
  handleSearch: () => void;
  handleAddKeyword: () => void;
  saveRuleButtonRef?: React.RefObject<HTMLButtonElement | null>;
}

// Helper function to get translated chip name
const getTranslatedChipName = (chip: string, t: (key: string) => string): string => {
  return t(`setMyAudience.filterCriteria.names.${chip}`);
};

// Helper function to get translated chip description
const getTranslatedChipDescription = (chip: string, t: (key: string) => string): string => {
  return t(`setMyAudience.filterCriteria.descriptions.${chip}`);
};

const getIcon = (type: string): React.ReactElement => {
  switch (type) {
    case 'User':
    case 'User (followers)':
    case 'User (specific user)':
    case 'User Posts (likers)':
    case 'User (followings)':
      return <User size={20} className="text-blue-600" />;
    case '#Hashtags':
    case '#Hashtags Posts (likers)':
      return <Hash size={20} className="text-purple-600" />;
    case 'Places':
    case 'Places Posts (likers)':
      return <MapPin size={20} className="text-red-500" />;
    case 'Explore':
    case 'Explore (likers)':
      return <Search size={20} className="text-green-600" />;
    case 'Posts by Keyword':
    case 'Posts by Keyword (likers)':
      return <List size={20} className="text-yellow-600" />;
    case 'Reels by Keyword':
    case 'Reels by Keyword (likers)':
      return <Film size={20} className="text-pink-600" />;
    case 'Self timeline':
    case 'Self timeline (likers)':
      return <Layers size={20} className="text-indigo-600" />;
    default:
      return <MessageSquare size={20} className="text-gray-600" />;
  }
};

const SetMyAudience: React.FC<Props> = ({
  chips,
  selectedChip,
  handleSelectChip,
  searchTerm,
  setSearchTerm,
  addFilterItem,
  audienceItems,
  selectedAudience,
  handleAudienceChange,
  filterItem,
  removeFilterItem,
  setFilterItems,
  openModal,
  notify,
  isSearching = false,
  handleSearch,
  handleAddKeyword,
  saveRuleButtonRef,
}) => {
  const { t } = useTranslation();
  // Get minimum filter count from environment variable (only for "User (specific user)" criteria)
  const envMinFilterCount = parseInt(process.env.REACT_APP_MIN_FILTER_COUNT || '10', 10);
  const defaultMinFilterCount = 10;

  // Determine minFilterCount based on filterItem contents:
  // - Use env variable only if ALL items are "User (specific user)"
  // - Use 10 for all other cases (other types or mixed types)
  const minFilterCount = React.useMemo(() => {
    if (filterItem.length === 0) {
      // Default to env value when no items yet (will be recalculated when items are added)
      return envMinFilterCount;
    }
    const allSpecificUser = filterItem.every((item: any) => item.type === 'User (specific user)');
    return allSpecificUser ? envMinFilterCount : defaultMinFilterCount;
  }, [filterItem, envMinFilterCount]);

  // Tutorial context
  const { currentStep, nextStep, isTutorialActive } = useTutorial();

  // Ref for search results dropdown
  const searchResultsRef = useRef<HTMLDivElement>(null);
  const [showSearchResults, setShowSearchResults] = React.useState(false);
  const [viewedItem, setViewedItem] = React.useState<any | null>(null);
  const [viewCardPosition, setViewCardPosition] = React.useState<{ x: number; y: number } | null>(null);

  // Tutorial refs
  const firstChipRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchButtonRef = useRef<HTMLButtonElement>(null);
  const firstCheckboxRef = useRef<HTMLInputElement>(null);
  const addSelectedButtonRef = useRef<HTMLButtonElement>(null);

  // Show search results when items are available
  useEffect(() => {
    if (audienceItems.length > 0) {
      setShowSearchResults(true);
    }
  }, [audienceItems]);

  // Handle click outside to close search results
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // Don't close if clicking on the viewed card
      if (target.closest('[data-viewed-card]')) {
        return;
      }
      if (searchResultsRef.current && !searchResultsRef.current.contains(event.target as Node)) {
        setShowSearchResults(false);
      }
    };

    if (showSearchResults) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSearchResults]);

  // Handle click outside to close viewed cards
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // Check if click is outside the viewed card
      if (viewedItem) {
        // Don't close if clicking on the thumbnail or the card itself
        if (!target.closest('[data-viewed-card]') && !target.closest('.famous-clicker-audience-filter-img')) {
          setViewedItem(null);
          setViewCardPosition(null);
        }
      }
    };

    if (viewedItem) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [viewedItem]);

  // Tutorial: Handle chip selection
  const handleChipClick = (chip: string) => {
    handleSelectChip(chip);
    if (isTutorialActive && currentStep === TutorialStep.SELECT_FILTER) {
      setTimeout(() => {
        nextStep(); // Move to SEARCH_INPUT step
        // Scroll to search input
        setTimeout(() => {
          searchInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          searchInputRef.current?.focus();
        }, 300);
      }, 500);
    }
  };

  // Tutorial: Handle search term change
  useEffect(() => {
    if (isTutorialActive && currentStep === TutorialStep.SEARCH_INPUT && searchTerm.length > 0) {
      setTimeout(() => {
        nextStep(); // Move to SEARCH_BUTTON step
        setTimeout(() => {
          searchButtonRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 300);
      }, 500);
    }
  }, [searchTerm, currentStep, isTutorialActive, nextStep]);

  // Tutorial: Handle search results appearing
  useEffect(() => {
    if (isTutorialActive && currentStep === TutorialStep.SEARCH_BUTTON && audienceItems.length > 0) {
      setTimeout(() => {
        nextStep(); // Move to SELECT_CHECKBOX step
        setTimeout(() => {
          firstCheckboxRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 300);
      }, 500);
    }
  }, [audienceItems.length, currentStep, isTutorialActive, nextStep]);

  // Tutorial: Handle checkbox selection
  useEffect(() => {
    if (isTutorialActive && currentStep === TutorialStep.SELECT_CHECKBOX && selectedAudience.length > 0) {
      setTimeout(() => {
        nextStep(); // Move to ADD_SELECTED_ITEMS step
        setTimeout(() => {
          addSelectedButtonRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 300);
      }, 500);
    }
  }, [selectedAudience.length, currentStep, isTutorialActive, nextStep]);

  // Validation function for saving rules
  const validateAndSave = () => {
    if (filterItem.length === 0) {
      notify(t('setMyAudience.errors.addOneFilter'));
      return;
    }

    if (filterItem.length < minFilterCount) {
      notify(t('setMyAudience.errors.minimumFilters', { minFilterCount, currentCount: filterItem.length }));
      return;
    }

    // Tutorial: Move to RULE_NAME_INPUT step when modal opens
    if (isTutorialActive && currentStep === TutorialStep.SAVE_RULE) {
      setTimeout(() => {
        nextStep();
      }, 500);
    }

    // If validation passes, open the modal
    openModal();
  };

  // Check if selected chip is a keyword-based chip
  const isKeywordChip = selectedChip && [
    'Posts by Keyword',
    'Posts by Keyword (likers)',
    'Reels by Keyword',
    'Reels by Keyword (likers)'
  ].includes(selectedChip);

  return (
    <div className="famous-clicker-audience-container">
      <div className="famous-clicker-audience-container-scroll">
        

        {/* Quick Start Guide */}
        <div className="mb-4 p-3 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg">
          <div className="flex items-start gap-2">
            <i className="bi bi-compass text-green-600 text-sm mt-0.5"></i>
            <div className="flex-1">
              <p className="text-xs font-semibold text-gray-800 mb-1">{t('setMyAudience.titles.quickStart')}</p>
              <ol className="text-xs text-gray-700 space-y-1 ml-3" style={{ listStyleType: 'decimal' }}>
                <li>{t('setMyAudience.messages.step1')}</li>
                <li>{t('setMyAudience.messages.step2')}</li>
                <li>{t('setMyAudience.messages.step3', { minFilterCount })}</li>
              </ol>
            </div>
          </div>
        </div>

        {/* Category Chips */}
        <div className="famous-clicker-audience-category-container">
          <div className="famous-clicker-audience-category-subheader">{t('setMyAudience.titles.filteringCriteria')}</div>
          <div className="famous-clicker-audience-category-chips">
            {chips.map((chip: string, index: number) => (
              <div
                key={index}
                ref={index === 0 ? firstChipRef : null}
                className={`famous-clicker-audience-category-chip ${
                  selectedChip === chip ? 'famous-clicker-audience-selected' : ''
                }`}
                onClick={() => handleChipClick(chip)}
                style={{
                  boxShadow: isTutorialActive && currentStep === TutorialStep.SELECT_FILTER && index === 0
                    ? '0 0 0 4px rgba(99, 179, 237, 0.5)'
                    : 'none',
                  animation: isTutorialActive && currentStep === TutorialStep.SELECT_FILTER && index === 0
                    ? 'pulse-glow 2s infinite'
                    : 'none'
                }}
              >
                {getTranslatedChipName(chip, t)}
              </div>
            ))}
          </div>

          {/* Improved Chip Description */}
          {selectedChip && (
           <div className="mt-4 p-4 rounded-xl border border-gray-200 shadow-sm bg-gradient-to-r from-blue-50 to-white">
  <div className="flex items-start space-x-3">
    <div className="flex-shrink-0">{getIcon(selectedChip)}</div>
    <div>
      <h3 className="text-xs font-semibold text-gray-800">{getTranslatedChipName(selectedChip, t)}</h3>
      <p style={{ fontSize: "11px", color: "#9ca3af", lineHeight: "1.4" }}>
        {getTranslatedChipDescription(selectedChip, t)}
      </p>

      {/* Disclaimer / Info Note */}
      <div
        style={{
          fontSize: "10px",
          color: "#6b7280",
          marginTop: "6px",
          paddingLeft: "4px",
          borderLeft: "2px solid #93c5fd",
        }}
      >
        {t('setMyAudience.messages.ruleSelectionNote')}
      </div>
    </div>
  </div>
</div>

          )}
        </div>

        {/* Filter Items - Progress Bar Section */}
        <div className="famous-clicker-audience-filter-section" style={{ marginBottom: '16px' }}>
          <div className="famous-clicker-audience-filter-header">
            <div className="flex items-center gap-2 flex-1">
              <i className="bi bi-collection text-gray-600"></i>
              <span className="text-xs font-medium text-gray-700">
                {t('setMyAudience.labels.selectedFilters')}
              </span>
              <div className="flex-1 max-w-[200px]">
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className={`h-full transition-all duration-300 rounded-full ${
                      filterItem.length >= minFilterCount
                        ? 'bg-green-500'
                        : filterItem.length >= minFilterCount * 0.5
                          ? 'bg-yellow-500'
                          : 'bg-blue-500'
                    }`}
                    style={{ width: `${Math.min((filterItem.length / minFilterCount) * 100, 100)}%` }}
                  />
                </div>
              </div>
              {filterItem.length >= minFilterCount && (
                <i className="bi bi-check-circle-fill text-green-500"></i>
              )}
            </div>
            {filterItem.length < minFilterCount && (
              <span className="famous-clicker-audience-filter-warning">
                <i className="bi bi-exclamation-circle" style={{ marginRight: '4px' }}></i>
                {t('setMyAudience.messages.selectUntilFull')}
              </span>
            )}
            {filterItem.length >= minFilterCount && (
              <span className="text-xs text-green-600 font-medium flex items-center gap-1">
                {t('setMyAudience.messages.readyToSave')}
              </span>
            )}
          </div>
        </div>

        {/* Search Box */}
        <div style={{ position: 'relative' }}>
          <div className="famous-clicker-audience-search-box">
            <div style={{ position: 'relative', flex: 1 }}>
              <input
                ref={searchInputRef}
                type="text"
                placeholder={isKeywordChip ? 'Enter keyword to add as filter' : t('setMyAudience.placeholders.search')}
                id="searchInput"
                value={searchTerm}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    if (isKeywordChip) {
                      handleAddKeyword();
                    } else {
                      handleSearch();
                    }
                  }
                }}
                style={{
                  boxShadow: isTutorialActive && currentStep === TutorialStep.SEARCH_INPUT
                    ? '0 0 0 4px rgba(99, 179, 237, 0.5)'
                    : 'none'
                }}
              />
              {isSearching && (
                <div
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                  }}
                >
                  <Spinner size="small" color="primary" />
                </div>
              )}
            </div>
            <button
              ref={searchButtonRef}
              className="famous-clicker-audience-search-button"
              onClick={isKeywordChip ? handleAddKeyword : handleSearch}
              disabled={isSearching}
              style={{
                boxShadow: isTutorialActive && currentStep === TutorialStep.SEARCH_BUTTON
                  ? '0 0 0 4px rgba(99, 179, 237, 0.5)'
                  : 'none',
                animation: isTutorialActive && currentStep === TutorialStep.SEARCH_BUTTON
                  ? 'pulse-glow 2s infinite'
                  : 'none'
              }}
            >
              <i className={isKeywordChip ? "bi bi-plus-circle" : "bi bi-search"}></i>
              {isKeywordChip ? 'Add' : t('setMyAudience.buttons.search')}
            </button>
          </div>

          {/* Search Results Dropdown */}
          {showSearchResults && audienceItems.length > 0 && (
            <div
              ref={searchResultsRef}
              className="famous-clicker-audience-search-results-dropdown"
            >
              {/* Sticky Header with selection count and Add button */}
              <div className="famous-clicker-audience-search-results-header" style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span className="famous-clicker-audience-search-results-count">
                  {t('setMyAudience.messages.resultsFound', { count: audienceItems.length })}
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {/* Add button moved to header - always visible */}
                  <button
                    ref={addSelectedButtonRef}
                    className="famous-clicker-audience-add-item-button"
                    onClick={() => {
                      addFilterItem();
                      setTimeout(() => {
                        setShowSearchResults(false);
                      }, 300);
                    }}
                    disabled={selectedAudience.length === 0}
                    style={{
                      padding: '6px 12px',
                      fontSize: '12px',
                      boxShadow: isTutorialActive && currentStep === TutorialStep.ADD_SELECTED_ITEMS
                        ? '0 0 0 4px rgba(99, 179, 237, 0.5)'
                        : 'none',
                      animation: isTutorialActive && currentStep === TutorialStep.ADD_SELECTED_ITEMS
                        ? 'pulse-glow 2s infinite'
                        : 'none'
                    }}
                  >
                    <i className="bi bi-plus-circle" style={{ marginRight: '4px' }}></i>
                    {t('setMyAudience.buttons.addSelected')} {selectedAudience.length > 0 && `(${selectedAudience.length})`}
                  </button>
                  <button
                    className="famous-clicker-audience-search-results-close"
                    onClick={() => setShowSearchResults(false)}
                  >
                    <i className="bi bi-x-lg"></i>
                  </button>
                </div>
              </div>

              <div className="famous-clicker-audience-search-results-list" id="audienceList">
            {audienceItems.map((item: any) => {
              // Determine if this is a posts/reels search result
              // Exclude 'Places Posts (likers)' from being treated as posts/reels
              const isPostsOrReels = selectedChip &&
                (selectedChip === 'Posts by Keyword' ||
                 selectedChip === 'Posts by Keyword (likers)' ||
                 selectedChip === 'Reels by Keyword' ||
                 selectedChip === 'Reels by Keyword (likers)');

              const keyValue = isPostsOrReels
                ? item.id
                : selectedChip && selectedChip.toLowerCase().includes('user')
                ? item.username
                : selectedChip && selectedChip.toLowerCase().includes('hashtag')
                ? item.id
                : item.locationId;

              const isChecked = selectedAudience.some(
                (aud: any) => aud.id === keyValue
              );

              const handleChangeID = keyValue;

              const filterName = isPostsOrReels
                ? item.caption || 'No caption'
                : selectedChip && selectedChip.toLowerCase().includes('user')
                ? item.full_name
                : selectedChip && selectedChip.toLowerCase().includes('hashtag')
                ? item.name
                : item.locationName;

              const pk = isPostsOrReels
                ? item.id
                : selectedChip && selectedChip.toLowerCase().includes('user')
                ? item.id
                : selectedChip && selectedChip.toLowerCase().includes('hashtag')
                ? item.id
                : item.locationId;

              const isFirstItem = audienceItems.indexOf(item) === 0;

              return (
                <div
                  key={keyValue}
                  className="famous-clicker-audience-audience-item"
                >
                  <input
                    ref={isFirstItem ? firstCheckboxRef : null}
                    type="checkbox"
                    id={keyValue}
                    checked={isChecked}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      handleAudienceChange(
                        handleChangeID,
                        e.target.checked,
                        filterName,
                        pk,
                        isPostsOrReels ? { caption: item.caption, thumbnail: item.thumbnail } : undefined
                      )
                    }
                    style={{
                      boxShadow: isTutorialActive && currentStep === TutorialStep.SELECT_CHECKBOX && isFirstItem
                        ? '0 0 0 4px rgba(99, 179, 237, 0.5)'
                        : 'none',
                      animation: isTutorialActive && currentStep === TutorialStep.SELECT_CHECKBOX && isFirstItem
                        ? 'pulse-glow 2s infinite'
                        : 'none'
                    }}
                  />
                  {isPostsOrReels ? (
                    <img
                      className="famous-clicker-audience-filter-img"
                      src={item.thumbnail}
                      alt="Post/Reel thumbnail"
                      style={{
                        objectFit: 'cover',
                        cursor: 'pointer'
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        const rect = e.currentTarget.getBoundingClientRect();
                        setViewedItem(viewedItem?.id === item.id ? null : item);
                        setViewCardPosition(viewedItem?.id === item.id ? null : { x: rect.right + 10, y: rect.top });
                      }}
                    />
                  ) : selectedChip && selectedChip.toLowerCase().includes('user') ? (
                    <img
                      className="famous-clicker-audience-filter-img"
                      src={item.profile_pic_url}
                      alt="Profile"
                    />
                  ) : null}
                  <div className="famous-clicker-audience-user-info">
                    {isPostsOrReels ? (
                      <>
                        <h3 style={{ fontSize: '12px', fontWeight: '600' }}>
                          {item.user?.username || 'Unknown user'}
                        </h3>
                        <p style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>
                          {item.caption ? (item.caption.length > 50 ? item.caption.substring(0, 50) + '...' : item.caption) : 'No caption'}
                        </p>
                        <div style={{ display: 'flex', gap: '8px', marginTop: '4px', fontSize: '10px', color: '#9ca3af' }}>
                          <span><i className="bi bi-heart" style={{ marginRight: '2px' }}></i>{item.like_count || 0}</span>
                          <span><i className="bi bi-chat" style={{ marginRight: '2px' }}></i>{item.comment_count || 0}</span>
                        </div>
                      </>
                    ) : (
                      <>
                        <h3>
                          {selectedChip && selectedChip.toLowerCase().includes('user')
                            ? item.full_name
                            : selectedChip && selectedChip.toLowerCase().includes('hashtag')
                            ? item.name
                            : item.locationName}
                        </h3>
                        <p>
                          {selectedChip && selectedChip.toLowerCase().includes('user')
                            ? item.username
                            : selectedChip && selectedChip.toLowerCase().includes('hashtag')
                            ? item.search_result_subtitle
                            : item.subtitle}
                        </p>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
              </div>
            </div>
          )}

          {/* Viewed Card for Posts/Reels - Search Results (Click to View) */}
          {viewedItem && viewCardPosition && (() => {
            const cardWidth = 320;
            const cardHeight = 400;
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;

            // Adjust position to keep card in viewport
            let adjustedX = viewCardPosition.x;
            let adjustedY = viewCardPosition.y;

            if (adjustedX + cardWidth > viewportWidth) {
              adjustedX = viewportWidth - cardWidth - 10;
            }
            if (adjustedY + cardHeight > viewportHeight) {
              adjustedY = viewportHeight - cardHeight - 10;
            }

            return (
              <div
                data-viewed-card="true"
                style={{
                  position: 'fixed',
                  left: `${adjustedX}px`,
                  top: `${adjustedY}px`,
                  zIndex: 10001,
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
                  padding: '0',
                  width: '320px',
                  maxHeight: '400px',
                  overflow: 'hidden',
                  border: '2px solid #3b82f6'
                }}
              >
                {/* Close Button */}
                <button
                  onClick={() => {
                    setViewedItem(null);
                    setViewCardPosition(null);
                  }}
                  style={{
                    position: 'absolute',
                    top: '8px',
                    right: '8px',
                    zIndex: 10,
                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '50%',
                    width: '28px',
                    height: '28px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '16px'
                  }}
                >
                  <i className="bi bi-x"></i>
                </button>

                {/* Large Thumbnail */}
                <div style={{ width: '100%', height: '200px', overflow: 'hidden', backgroundColor: '#f3f4f6' }}>
                  <img
                    src={viewedItem.thumbnail}
                    alt="Post/Reel"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                </div>

                {/* Details */}
                <div style={{ padding: '16px' }}>
                  {/* User Info */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                    {viewedItem.user?.profile_pic_url && (
                      <img
                        src={viewedItem.user.profile_pic_url}
                        alt="Profile"
                        style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '50%',
                          objectFit: 'cover'
                        }}
                      />
                    )}
                    <div>
                      <div style={{ fontWeight: '600', fontSize: '13px', color: '#111827' }}>
                        {viewedItem.user?.username || 'Unknown user'}
                      </div>
                      {viewedItem.user?.full_name && (
                        <div style={{ fontSize: '11px', color: '#6b7280' }}>
                          {viewedItem.user.full_name}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Caption */}
                  <div style={{ marginBottom: '12px' }}>
                    <p style={{
                      fontSize: '12px',
                      color: '#374151',
                      lineHeight: '1.5',
                      margin: 0,
                      maxHeight: '60px',
                      overflow: 'auto'
                    }}>
                      {viewedItem.caption || 'No caption'}
                    </p>
                  </div>

                  {/* Engagement Stats */}
                  <div style={{
                    display: 'flex',
                    gap: '16px',
                    paddingTop: '12px',
                    borderTop: '1px solid #e5e7eb'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <i className="bi bi-heart-fill" style={{ color: '#ef4444', fontSize: '14px' }}></i>
                      <span style={{ fontSize: '12px', fontWeight: '600', color: '#374151' }}>
                        {viewedItem.like_count?.toLocaleString() || 0}
                      </span>
                      <span style={{ fontSize: '11px', color: '#9ca3af' }}>likes</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <i className="bi bi-chat-fill" style={{ color: '#3b82f6', fontSize: '14px' }}></i>
                      <span style={{ fontSize: '12px', fontWeight: '600', color: '#374151' }}>
                        {viewedItem.comment_count?.toLocaleString() || 0}
                      </span>
                      <span style={{ fontSize: '11px', color: '#9ca3af' }}>comments</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>

        {/* Filter Items List */}
        <div className="famous-clicker-audience-filter-section">
          {filterItem.length === 0 ? (
            <div className="famous-clicker-audience-no-filters">
              <div className="text-center py-6">
                <i className="bi bi-funnel text-gray-300 text-3xl mb-2"></i>
                <p className="text-xs text-gray-600 font-medium mb-1">{t('setMyAudience.messages.noFilters')}</p>
                <p className="text-xs text-gray-500">{t('setMyAudience.messages.startBuilding')}</p>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {filterItem.map((item: any, index: number) => {
                const hasMedia = item.thumbnail && item.thumbnail.trim() !== '';
                const displayCaption = item.caption || 'No caption';
                return (
                  <div
                    className="famous-clicker-audience-filters-chip"
                    id={`chip-recurring-${index}`}
                    key={index}
                  >
                    {hasMedia ? (
                      <img
                        src={item.thumbnail}
                        alt="Post/Reel thumbnail"
                        style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '6px',
                          objectFit: 'cover',
                          flexShrink: 0
                        }}
                        onError={(e) => {
                          // If image fails to load, replace with placeholder
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    ) : (
                      getIcon(item.type)
                    )}
                    <div className="flex-1">
                      <span className="font-medium text-xs">
                        {hasMedia ? (displayCaption.length > 50 ? displayCaption.substring(0, 50) + '...' : displayCaption) : item.filter}
                      </span>
                      <p className="text-xs text-gray-500 mt-0.5">{getTranslatedChipName(item.type, t)}</p>
                    </div>
                    <button
                      className="famous-clicker-audience-filters-close-button"
                      onClick={() => removeFilterItem(index)}
                    >
                      <i className="bi bi-x"></i>
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="famous-clicker-audience-actions" style={{ flexShrink: 0 }}>
        <button
          className="famous-clicker-audience-secondary flex items-center gap-2"
          onClick={() => setFilterItems([])}
        >
          <i className="bi bi-x-circle"></i>
          {t('setMyAudience.buttons.clearAll')}
        </button>
        <button
          ref={saveRuleButtonRef}
          className={`famous-clicker-audience-primary flex items-center gap-2 ${
            filterItem.length < minFilterCount ? 'famous-clicker-audience-disabled' : ''
          }`}
          onClick={validateAndSave}
          disabled={filterItem.length < minFilterCount}
          style={{
            boxShadow: isTutorialActive && currentStep === TutorialStep.SAVE_RULE
              ? '0 0 0 4px rgba(99, 179, 237, 0.5)'
              : 'none',
            animation: isTutorialActive && currentStep === TutorialStep.SAVE_RULE
              ? 'pulse-glow 2s infinite'
              : 'none'
          }}
        >
          <i className="bi bi-save"></i>
          {t('setMyAudience.buttons.saveRule')}
          {filterItem.length >= minFilterCount && (
            <span className="ml-1 px-1.5 py-0.5 bg-white/20 rounded text-xs">
              {filterItem.length}
            </span>
          )}
        </button>
      </div>

      {/* Tutorial Tooltips */}
      <GuideTooltip
        targetRef={firstChipRef}
        message={t('setMyAudience.tutorial.selectFilter')}
        show={isTutorialActive && currentStep === TutorialStep.SELECT_FILTER}
      />
      <GuideTooltip
        targetRef={searchInputRef}
        message={t('setMyAudience.tutorial.searchInput')}
        show={isTutorialActive && currentStep === TutorialStep.SEARCH_INPUT}
      />
      <GuideTooltip
        targetRef={searchButtonRef}
        message={t('setMyAudience.tutorial.searchButton')}
        show={isTutorialActive && currentStep === TutorialStep.SEARCH_BUTTON}
      />
      <GuideTooltip
        targetRef={firstCheckboxRef}
        message={t('setMyAudience.tutorial.selectCheckbox')}
        show={isTutorialActive && currentStep === TutorialStep.SELECT_CHECKBOX}
      />
      <GuideTooltip
        targetRef={addSelectedButtonRef}
        message={t('setMyAudience.tutorial.addSelectedItems')}
        show={isTutorialActive && currentStep === TutorialStep.ADD_SELECTED_ITEMS}
      />
      {saveRuleButtonRef && (
        <GuideTooltip
          targetRef={saveRuleButtonRef}
          message={t('setMyAudience.tutorial.saveRule')}
          show={isTutorialActive && currentStep === TutorialStep.SAVE_RULE}
        />
      )}
    </div>
  );
};

export default SetMyAudience;
